import { z } from "zod";
import { bookProductionProductSchema } from "./book-production-evidence.js";
export const BOOK_PRINT_PROFILE_CONTRACT = "BOOK_PRINT_PROFILE_V1";
const safeIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(220)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.:/-]*$/u);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const timestampSchema = z.string().datetime({ offset: true });
const pointSizeSchema = z
    .object({ width: z.number().positive(), height: z.number().positive() })
    .strict();
const millimetreInsetsSchema = z
    .object({
    top: z.number().nonnegative(),
    right: z.number().nonnegative(),
    bottom: z.number().nonnegative(),
    left: z.number().nonnegative(),
})
    .strict();
const coverRegionSchema = z
    .object({
    xMm: z.number().nonnegative(),
    yMm: z.number().nonnegative(),
    widthMm: z.number().positive(),
    heightMm: z.number().positive(),
})
    .strict();
export const bookPrintEndpointCountKindSchema = z.enum([
    "CATALOG_PRINTABLE_INTERIOR",
    "COVER_DIMENSIONS_REQUEST",
    "COVER_DIMENSIONS_RESPONSE",
    "QUOTE_ITEM",
    "ORDER_ITEM",
    "UPLOADED_PDF",
]);
export const bookPrintEndpointCountEvidenceV1Schema = z
    .object({
    kind: bookPrintEndpointCountKindSchema,
    pageCount: z.number().int().positive().max(1_000),
    state: z.enum(["SESSION_RECORDED_NOT_ARCHIVED", "VERIFIED_ARCHIVED_RESPONSE"]),
    endpointVersion: safeIdSchema,
    requestDigestSha256: sha256Schema.nullable(),
    responseDigestSha256: sha256Schema.nullable(),
    capturedAt: timestampSchema,
})
    .strict()
    .superRefine((evidence, context) => {
    if (evidence.state === "VERIFIED_ARCHIVED_RESPONSE" &&
        (!evidence.requestDigestSha256 || !evidence.responseDigestSha256))
        context.addIssue({
            code: "custom",
            path: ["responseDigestSha256"],
            message: "Verified endpoint evidence requires archived request and response digests.",
        });
});
export const bookPrintProfileV1Schema = z
    .object({
    contract: z.literal(BOOK_PRINT_PROFILE_CONTRACT),
    profileId: safeIdSchema,
    profileVersion: safeIdSchema,
    editionPolicyId: safeIdSchema,
    product: bookProductionProductSchema,
    activationState: z.enum(["ACTIVATION_BLOCKED", "ACTIVE"]),
    activationBlockers: z.array(safeIdSchema).max(50),
    providerBinding: z
        .object({
        provider: z.literal("GELATO"),
        environment: z.literal("PRODUCTION"),
        productUid: safeIdSchema,
        country: z.literal("US"),
        endpointCounts: z.array(bookPrintEndpointCountEvidenceV1Schema).length(6),
    })
        .strict(),
    geometry: z
        .object({
        nominalTrimMm: z
            .object({ width: z.number().positive(), height: z.number().positive() })
            .strict(),
        interiorPdfMediaBoxPoints: pointSizeSchema,
        inferredBleedMm: millimetreInsetsSchema,
        requiredSafeInsetsMm: millimetreInsetsSchema,
        geometryEvidenceState: z.enum([
            "SESSION_RECORDED_NOT_ARCHIVED",
            "VERIFIED_EXACT_PRODUCT_TEMPLATE",
        ]),
        exactTemplateDigestSha256: sha256Schema.nullable(),
        coverPdfMediaBoxPoints: pointSizeSchema,
        coverRegionsMm: z
            .object({
            wraparoundInside: coverRegionSchema,
            wraparoundEdge: coverRegionSchema,
            back: coverRegionSchema,
            jointBack: coverRegionSchema,
            spine: coverRegionSchema,
            jointFront: coverRegionSchema,
            front: coverRegionSchema,
            barcodeSafe: coverRegionSchema,
        })
            .strict()
            .nullable(),
    })
        .strict(),
    pdfPolicy: z
        .object({
        conformance: z.literal("PDF_X_4"),
        outputIntent: z.literal("GRACOL_2006"),
        fontsEmbedded: z.literal(true),
        liveSearchableTypeWhereSupported: z.literal(true),
        cropMarks: z.literal(false),
        passwordProtection: z.literal(false),
        minimumEffectiveImageDpi: z.number().int().min(150).max(300),
        targetEffectiveImageDpi: z.literal(300),
        generalGuidanceBleedMm: z.literal(4),
    })
        .strict(),
    physicalSample: z
        .object({
        state: z.enum(["NOT_ORDERED", "ORDERED", "REVIEWED_REJECTED", "REVIEWED_APPROVED"]),
        evidenceDigestSha256: sha256Schema.nullable(),
        reviewedAt: timestampSchema.nullable(),
    })
        .strict(),
    truthBoundary: z.literal("PROFILE_BINDS_SUPPLIED_PROVIDER_EVIDENCE_BUT_DOES_NOT_AUTHENTICATE_PROVIDER_STATE"),
})
    .strict()
    .superRefine((profile, context) => {
    const kinds = profile.providerBinding.endpointCounts.map(({ kind }) => kind);
    if (new Set(kinds).size !== bookPrintEndpointCountKindSchema.options.length)
        context.addIssue({
            code: "custom",
            path: ["providerBinding", "endpointCounts"],
            message: "Every provider endpoint count must be represented exactly once.",
        });
    const isActive = profile.activationState === "ACTIVE";
    const allCountsVerified = profile.providerBinding.endpointCounts.every(({ state }) => state === "VERIFIED_ARCHIVED_RESPONSE");
    if (isActive &&
        (profile.activationBlockers.length > 0 ||
            !allCountsVerified ||
            profile.geometry.geometryEvidenceState !== "VERIFIED_EXACT_PRODUCT_TEMPLATE" ||
            !profile.geometry.exactTemplateDigestSha256 ||
            !profile.geometry.coverRegionsMm ||
            profile.physicalSample.state !== "REVIEWED_APPROVED" ||
            !profile.physicalSample.evidenceDigestSha256 ||
            !profile.physicalSample.reviewedAt))
        context.addIssue({
            code: "custom",
            path: ["activationState"],
            message: "An active print profile requires archived endpoint evidence, exact template geometry, no blockers, and an approved physical sample.",
        });
    if (!isActive && profile.activationBlockers.length === 0)
        context.addIssue({
            code: "custom",
            path: ["activationBlockers"],
            message: "A blocked profile must retain at least one explicit activation blocker.",
        });
});
//# sourceMappingURL=book-print-profile.js.map